import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, RefreshControl, ScrollView, TextInput, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useChatInputStore, useCollectionStore, useFileStore, useLightboxStore, useModalStore, useToastStore } from '@/store';
import { useHasUser } from '@/hooks/useRequireAuth';
import useResponsiveness from '@/hooks/useResponsiveness';
import useFilesLibrary from '@/hooks/useFilesLibrary';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalHelper } from '@/hooks/useModalHelper';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import LibraryFileItem, { FILE_CATEGORIES } from '@/components/Files/LibraryFileItem';
import { getLibraryPalette } from '@/components/Files/libraryPalette';
import Tooltip from '@/components/Tooltip';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { isCodeFile, isEditableDocumentFile, isEditorImportableFile, isLightboxCompatibleFile, isPdfFile, isPresentationFile, isSpreadsheetFile, openMessageFile } from '@/constants/utils/files';

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest first', icon: 'arrow-down-wide-short' },
  { id: 'oldest', label: 'Oldest first', icon: 'arrow-up-wide-short' },
  { id: 'a-z', label: 'Name A–Z', icon: 'arrow-down-a-z' },
  { id: 'z-a', label: 'Name Z–A', icon: 'arrow-up-z-a' },
];
const SOURCE_OPTIONS = [
  { id: 'all', label: 'All sources', icon: 'files' },
  { id: 'upload', label: 'Uploaded by you', icon: 'cloud-arrow-up' },
  { id: 'inference', label: 'AI generated', icon: 'sparkles' },
];
const GAP = 16;

function Action({ label, icon, onPress, primary, danger, disabled, compact }) {
  const { colors, theme } = useTheme();
  const palette = getLibraryPalette(theme);
  const color = primary ? palette.onInk : danger ? colors.color_destructive : colors.text_color_primary;
  return <Pressable accessibilityRole="button" accessibilityLabel={label} disabled={disabled} onPress={onPress}
    style={({ hovered, pressed }) => ({ minHeight: 40, paddingHorizontal: compact ? 12 : 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 10, borderWidth: 1, borderColor: primary ? palette.ink : palette.border, backgroundColor: primary ? palette.ink : hovered || pressed ? palette.hover : palette.surface, opacity: disabled ? 0.5 : primary && (hovered || pressed) ? 0.8 : 1 })}>
    {icon && <Icon name={icon} size={14} color={color} />}
    <Text size="s" weight="medium" style={{ color }}>{label}</Text>
  </Pressable>;
}

function EmptyResult({ title, description, icon = 'search', action, onAction }) {
  const { theme } = useTheme();
  const palette = getLibraryPalette(theme);
  return <View style={{ alignItems: 'center', paddingVertical: 56, paddingHorizontal: 24, gap: 14 }}>
    <View style={{ padding: 20, borderRadius: 20, backgroundColor: palette.preview }}><Icon name={icon} size={28} color={palette.icon} /></View>
    <Text size="xl" weight="medium" style={{ textAlign: 'center', marginTop: 4 }}>{title}</Text>
    <Text size="s" color="secondary" style={{ textAlign: 'center', maxWidth: 420, lineHeight: 21 }}>{description}</Text>
    <Action label={action} onPress={onAction} />
  </View>;
}

export default function FilesPage() {
  const { colors, theme } = useTheme();
  const palette = getLibraryPalette(theme);
  const { t } = useTranslation();
  const categories = FILE_CATEGORIES.map(item => ({ ...item, label: item.id === 'all' ? t('pages.common.allFiles') : ['documents', 'images', 'audio', 'code'].includes(item.id) ? t(`pages.files.${item.id}`) : item.label }));
  const sortOptions = SORT_OPTIONS.map((item, index) => ({ ...item, label: t(`pages.common.${['newestFirst', 'oldestFirst', 'nameAZ', 'nameZA'][index]}`) }));
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const { isAuthResolved } = useHasUser();
  const token = useAuthStore(state => state.token);
  const isAuthed = Boolean(token);
  const emptyPreview = ['1', 'true', 'yes'].includes(String(params.empty || params.mockEmpty || '').toLowerCase());
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [source, setSource] = useState('all');
  const [sort, setSort] = useState('newest');
  const [view, setView] = useState('grid');
  const [contentWidth, setContentWidth] = useState(0);
  const [collectionsExpanded, setCollectionsExpanded] = useState(!isXS);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [openingFileId, setOpeningFileId] = useState(null);
  const openRequest = useRef(0);
  useEffect(() => () => { openRequest.current += 1; }, []);
  const library = useFilesLibrary({ query, category, source, sort, enabled: isAuthed && !emptyPreview });
  const filesMap = useFileStore(state => state.files);
  const deleteFile = useFileStore(state => state.actions.deleteFile);
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const fetchCollections = useCollectionStore(state => state.fetchCollections);
  const openModal = useModalStore(state => state.actions.openModal);
  const addToast = useToastStore(state => state.actions.addToast);
  const openLightbox = useLightboxStore(state => state.actions.openLightbox);
  const initializeNewChatWithFiles = useChatInputStore(state => state.initializeNewChatWithFiles);
  const { useConfirmationModal } = useModalHelper();
  const confirm = useConfirmationModal();

  useFocusEffect(useCallback(() => {
    if (isAuthed) void fetchCollections().catch(() => {});
  }, [fetchCollections, isAuthed]));

  // A changed scope must not leave invisible files selected for an action.
  useEffect(() => { setSelected(new Set()); }, [query, category, source, sort]);

  const files = useMemo(() => emptyPreview ? [] : library.ids.map(id => filesMap[id]).filter(Boolean), [library.ids, filesMap, emptyPreview]);
  const collections = useMemo(() => emptyPreview ? [] : collectionIds.map(id => collectionsMap[id]).filter(Boolean).filter(collection => !query.trim() || collection.title?.toLowerCase().includes(query.trim().toLowerCase())), [collectionIds, collectionsMap, query, emptyPreview]);
  const count = emptyPreview ? 0 : library.pagination.total || 0;
  const hasFilters = Boolean(query.trim() || category !== 'all' || source !== 'all');
  const columns = Math.max(1, Math.min(4, Math.floor((contentWidth + GAP) / (isXS ? 176 : 236))));
  const cardWidth = Math.max(0, (contentWidth - (columns - 1) * GAP) / columns);
  const compact = contentWidth < 680;

  const resetFilters = () => { setQuery(''); setCategory('all'); setSource('all'); };
  const details = useCallback(file => openModal('FileEditModal', { fileId: file.id }), [openModal]);
  const openFile = useCallback(async file => {
    const request = ++openRequest.current;
    const supported = isEditableDocumentFile(file) || isPresentationFile(file) || isSpreadsheetFile(file)
      || isEditorImportableFile(file) || isCodeFile(file) || isPdfFile(file) || isLightboxCompatibleFile(file);
    if (!supported || !file.cdnPath) { setOpeningFileId(null); details(file); return; }
    setOpeningFileId(file.id);
    try {
      // Match conversation attachments, including office import and image studio.
      await openMessageFile(file, { onOpenLightbox: openLightbox, throwOnError: true, shouldOpen: () => openRequest.current === request });
    } catch (error) {
      if (openRequest.current === request) addToast(error.message || 'Could not open this file. Please try again.', 'error');
    } finally { if (openRequest.current === request) setOpeningFileId(null); }
  }, [details, openLightbox, addToast]);
  const startChat = useCallback(file => {
    initializeNewChatWithFiles(file ? [file.id] : Array.from(selected));
    setSelected(new Set());
    router.push('/app');
  }, [selected, initializeNewChatWithFiles, router]);
  const collect = useCallback(file => {
    openModal('MoveToCollectionModal', { fileIds: file ? [file.id] : Array.from(selected), onSuccess: () => setSelected(new Set()) });
  }, [openModal, selected]);
  const download = useCallback(async file => {
    if (!file.cdnPath) return addToast('This file is not available to download yet.', 'error');
    try {
      await downloadMedia(file.cdnPath, file.name || 'download', { onSuccess: () => addToast('Download started', 'success'), onError: message => addToast(message || 'Download failed', 'error') });
    } catch (error) { addToast(error.message || 'Download failed', 'error'); }
  }, [addToast]);
  const toggleSelection = useCallback(file => {
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(file.id)) next.delete(file.id);
      else if (next.size < 10) next.add(file.id);
      return next;
    });
  }, []);
  const upload = useCallback(() => {
    // Uploading to the library should keep the user here, with their files visible.
    openModal('FileUploadModal', { libraryUpload: true, onUpload: result => {
      if (result.success) { resetFilters(); setSort('newest'); }
    } });
  }, [openModal]);
  const createCollection = () => openModal('CreateCollectionModal');
  const refresh = async () => {
    setRefreshing(true);
    try { await Promise.all([library.refresh(), isAuthed ? fetchCollections() : Promise.resolve()]); }
    finally { setRefreshing(false); }
  };
  const deleteSelected = () => confirm({
    title: `Delete ${selected.size === 1 ? 'file' : `${selected.size} files`}?`,
    message: 'These files will be removed from your library and collections. This cannot be undone.',
    confirmText: t('common.delete'), isDangerous: true,
    onConfirm: async () => {
      setDeleting(true);
      const failed = [];
      for (const id of selected) {
        try { await deleteFile(id); } catch (_) { failed.push(id); }
      }
      setDeleting(false);
      setSelected(new Set(failed));
      addToast(failed.length ? `${failed.length} file${failed.length === 1 ? '' : 's'} could not be deleted. Try again.` : 'Files deleted', failed.length ? 'error' : 'success');
      void library.refresh();
    },
  });

  return <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
    <MetaHead title={t('pages.files.title')} description={t('pages.files.description')} noindex />
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: selected.size ? 160 + insets.bottom : 40 + insets.bottom }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={colors.text_color_secondary} /> }>
      <View style={{ maxWidth: 1280, width: '100%', alignSelf: 'center', paddingHorizontal: isXS ? 16 : 32, paddingTop: isXS ? 12 : 32 }}>
        <View onLayout={event => setContentWidth(event.nativeEvent.layout.width)}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
            <View style={{ gap: 7, flex: 1, minWidth: 220 }}>
              {!isXS && <Text accessibilityRole="header" style={{ fontSize: 32, lineHeight: 38, letterSpacing: -0.8, fontWeight: '700' }}>{t('pages.files.title')}</Text>}
              <Text size="s" color="secondary" style={{ lineHeight: 20 }}>Your uploads and creations, in one place.</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Action label={t('pages.files.newCollection')} icon="folder-plus" onPress={createCollection} compact={isXS} />
              <Action label="Upload files" icon="cloud-arrow-up" onPress={upload} primary compact={isXS} />
            </View>
          </View>

          <View style={{ borderBottomWidth: 1, borderBottomColor: palette.border, paddingBottom: 16, marginBottom: 24, gap: 14 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', flexGrow: 1, flexBasis: compact ? '100%' : 280, gap: 10, minHeight: 42, paddingHorizontal: 13, borderRadius: 10, backgroundColor: palette.surface, borderWidth: 1, borderColor: searchFocused ? palette.activeBorder : palette.border }}>
                <Icon name="search" size={15} color={colors.text_color_secondary} />
                <TextInput value={query} onChangeText={setQuery} accessibilityLabel="Search files and collections" placeholder="Search files and collections…" placeholderTextColor={colors.text_color_tertiary}
                  autoCapitalize="none" autoCorrect={false} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
                  style={{ flex: 1, minWidth: 0, fontSize: 14, height: 40, color: colors.text_color_primary, ...(Platform.OS === 'web' && { outlineStyle: 'none' }) }} />
                {!!query && <Pressable accessibilityRole="button" accessibilityLabel="Clear search" onPress={() => setQuery('')} style={{ padding: 8 }}><Icon name="xmark" size={13} color={colors.text_color_secondary} /></Pressable>}
              </View>
              <CatalogSortDropdown label="Source" size="medium" value={source} options={SOURCE_OPTIONS} onChange={setSource} />
              <CatalogSortDropdown label="Sort" size="medium" value={sort} options={sortOptions} onChange={setSort} />
            </View>
            <ScrollView horizontal accessibilityRole="tablist" accessibilityLabel="File types" showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 5 }}>
              {categories.map(item => <Pressable key={item.id} accessibilityRole="tab" accessibilityLabel={item.label} accessibilityState={{ selected: category === item.id }} aria-selected={category === item.id} onPress={() => setCategory(item.id)}
                style={({ hovered }) => ({ minHeight: 34, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 11, gap: 7, borderRadius: 8, backgroundColor: category === item.id ? palette.hover : hovered ? palette.surface : 'transparent' })}>
                <Icon name={item.icon} size={13} color={category === item.id ? colors.text_color_primary : colors.text_color_tertiary} />
                <Text size="s" weight={category === item.id ? 'medium' : 'regular'} color={category === item.id ? 'primary' : 'secondary'}>{item.label}</Text>
              </Pressable>)}
            </ScrollView>
          </View>

          {collections.length > 0 && <View style={{ marginBottom: 24 }}>
            <Pressable accessibilityRole="button" accessibilityLabel={`${collectionsExpanded ? 'Collapse' : 'Expand'} collections`} accessibilityState={{ expanded: collectionsExpanded }} aria-expanded={collectionsExpanded} onPress={() => setCollectionsExpanded(value => !value)}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: collectionsExpanded ? 14 : 0 }}>
              <Icon name={collectionsExpanded ? 'chevron-down' : 'chevron-right'} size={10} color={colors.text_color_secondary} />
              <Text size="s" weight="medium">Collections</Text>
              <Text size="xs" color="tertiary">{collections.length}</Text>
            </Pressable>
            {collectionsExpanded && <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: GAP }}>
              {collections.map(collection => <Pressable key={collection.id} accessibilityRole="button" accessibilityLabel={`Open collection ${collection.title}`} onPress={() => router.push(`/collections/${collection.id}`)}
                style={({ hovered, pressed }) => ({ width: cardWidth, minHeight: 74, flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, borderColor: hovered ? palette.activeBorder : palette.border, backgroundColor: hovered || pressed ? palette.hover : palette.surface })}>
                <View style={{ width: 38, height: 38, alignItems: 'center', justifyContent: 'center', borderRadius: 10, backgroundColor: palette.preview }}><Icon name={collection.icon || 'folder'} size={18} color={palette.icon} /></View>
                <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
                  <Text size="s" weight="medium" numberOfLines={1}>{collection.title}</Text>
                  <Text size="xs" color="tertiary">{collection.files?.length || 0} {(collection.files?.length || 0) === 1 ? 'file' : 'files'} · {collection.conversationIds?.length || 0} {(collection.conversationIds?.length || 0) === 1 ? 'chat' : 'chats'}</Text>
                </View>
                <Icon name="chevron-right" size={10} color={colors.text_color_tertiary} />
              </Pressable>)}
            </View>}
          </View>}

          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text accessibilityRole="header" size="l" weight="semibold">{query.trim() ? 'Search results' : categories.find(item => item.id === category).label}</Text>
              <Text size="s" color="tertiary" accessibilityLiveRegion="polite">{library.loading ? 'Loading…' : count.toLocaleString()}</Text>
              <Tooltip label="Select up to 10 files to chat or organize.">
                <Pressable accessibilityRole="button" accessibilityLabel="About selecting files" onPress={() => addToast('Select up to 10 files to chat or organize.', 'info')} style={{ width: 28, height: 28, alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="circle-info" size={13} color={colors.text_color_tertiary} />
                </Pressable>
              </Tooltip>
              {hasFilters && <Pressable accessibilityRole="button" accessibilityLabel="Clear filters" onPress={resetFilters} style={{ padding: 6 }}><Text size="xs" color="secondary">Clear filters</Text></Pressable>}
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2, padding: 3, borderRadius: 10, borderWidth: 1, borderColor: palette.border, backgroundColor: palette.preview }}>
              {['grid', 'list'].map(mode => <Pressable key={mode} accessibilityRole="button" accessibilityLabel={`${mode === 'grid' ? 'Grid' : 'List'} view`} accessibilityState={{ selected: mode === view }} aria-pressed={mode === view} onPress={() => setView(mode)}
                style={{ width: 32, height: 30, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: view === mode ? palette.control : 'transparent' }}>
                <Icon name={mode === 'grid' ? 'grid-2' : 'bars'} size={14} color={view === mode ? colors.text_color_primary : colors.text_color_tertiary} />
              </Pressable>)}
            </View>
          </View>

          {openingFileId && <View accessibilityLiveRegion="polite" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}><ActivityIndicator size="small" color={colors.text_color_secondary} /><Text size="s" color="secondary">Opening file…</Text></View>}
          {library.loading ? <View style={{ padding: 64, alignItems: 'center', gap: 14 }}><ActivityIndicator color={colors.text_color_secondary} /><Text size="s" color="secondary">Loading your files…</Text></View>
            : library.error && !files.length ? <EmptyResult icon="circle-exclamation" title="We couldn’t load your files" description={library.error} action="Try again" onAction={library.refresh} />
            : !files.length ? <EmptyResult icon={hasFilters ? 'search' : 'folder-open'} title={hasFilters ? 'No matching files' : 'Your files, ready when you are'}
              description={hasFilters ? 'Try a different name, file type, or source.' : 'Upload files or save what you create in a conversation.'}
              action={hasFilters ? 'Clear filters' : 'Upload your first files'} onAction={hasFilters ? resetFilters : upload} />
            : <View style={view === 'grid' ? { flexDirection: 'row', flexWrap: 'wrap', gap: GAP } : { borderWidth: 1, borderColor: palette.border, borderRadius: 16, overflow: 'hidden' }}>
              {files.map(file => <LibraryFileItem key={file.id} file={file} width={cardWidth} list={view === 'list'} compact={compact}
                selected={selected.has(file.id)} selectionDisabled={selected.size >= 10 && !selected.has(file.id)} onSelect={toggleSelection}
                onOpen={openFile} onDetails={details} onDownload={download} onChat={startChat} onCollect={collect} />)}
            </View>}

          {!library.loading && files.length > 0 && <View style={{ alignItems: 'center', paddingTop: 24, gap: 12 }}>
            {library.error && <Text accessibilityRole="alert" size="s" style={{ color: colors.color_destructive }}>{library.error}</Text>}
            <Text size="xs" color="tertiary">Showing {files.length.toLocaleString()} of {count.toLocaleString()} files</Text>
            {library.pagination.hasMore && <Action label={library.loadingMore ? 'Loading more…' : 'Load more files'} onPress={library.loadMore} disabled={library.loadingMore} />}
          </View>}
          {Platform.OS === 'web' && !hasFilters && <Text size="xs" color="tertiary" style={{ textAlign: 'center', paddingTop: 24 }}>Drop files anywhere to add them to your library</Text>}
        </View>
      </View>
      {isAuthResolved && !isAuthed && <AppDashboardFooter fullViewportWidth />}
    </ScrollView>

    {selected.size > 0 && <View style={{ position: 'absolute', bottom: 20 + insets.bottom, left: isXS ? 12 : 24, right: isXS ? 12 : 24, maxWidth: 820, alignSelf: 'center', borderRadius: 16, borderWidth: 1, borderColor: palette.border, backgroundColor: palette.surface, padding: 14, gap: 12, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', ...(Platform.OS === 'web' && { boxShadow: '0 8px 32px #00000024' }) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <Pressable accessibilityRole="button" accessibilityLabel="Clear selection" onPress={() => setSelected(new Set())} style={{ padding: 8 }}><Icon name="xmark" size={14} color={colors.text_color_secondary} /></Pressable>
        <Text size="s" weight="medium">{selected.size} of 10 selected</Text>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        <Action label={t('pages.common.chat')} icon="message-dots" onPress={() => startChat()} primary disabled={deleting} />
        <Action label={t('dropdowns.addToCollection')} icon="folder-plus" onPress={() => collect()} disabled={deleting} compact />
        <Action label={deleting ? 'Deleting…' : t('common.delete')} icon="trash" onPress={deleteSelected} danger disabled={deleting} compact />
      </View>
    </View>}
  </View>;
}
